<script lang="ts">
    import { onMount } from 'svelte';
    import { JobModifyTimeWindowRuleApi, type JobModifyTimeWindowRuleRequestType } from './JobModifyTimeWindowRuleApi';
    import ButtonSmallBlue from '../../shared/ButtonSmallBlue.svelte';
    import InputText from '../../shared/InputText.svelte';
    import { api } from '../../../stores/api';
    import { get } from 'svelte/store';
    import Switch from '../../shared/Switch.svelte';
    import SelectHour from '../../shared/SelectHour.svelte';
    import SuccessInline from '../../shared/SuccessInline.svelte';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import { z } from 'zod';
    import SettingsRow from '../Settings/SettingsRow.svelte';
    import SettingsRowValue from '../Settings/SettingsRowValue.svelte';
    import SettingsRowLabel from '../Settings/SettingsRowLabel.svelte';
    import TemplateSettingsGeneral from '../Settings/TemplateSettingsGeneral.svelte';
    import SettingsRowNumber from '../Settings/SettingsRowNumber.svelte';
    import { closeModal } from 'svelte-modals';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import { _ } from 'svelte-i18n';

    export let componentProps: {
        onCreate: () => void
    }


    const defaultRule: JobModifyTimeWindowRuleRequestType = {
        enabled: true,
        address_id: null,
        name_part: '',
        start_of_time_window: null,
        end_of_time_window: '10:00',
    };

    let addressToFind: { city: string; street: string; house: string; pcode: string } = {
        city: '',
        street: '',
        house: '',
        pcode: '',
    };

    let newRule: JobModifyTimeWindowRuleRequestType = { ...defaultRule };

    let houseGeoId: string | null = null;
    let geocodedAddressData: any = null;
    let loading: boolean = false;
    let error: string = '';
    let createMessage: string = '';
    let existingRuleForAddress: any = null;
    let ruleExistsWarning: string = '';

    function handleCreate() {
        if (!newRule.start_of_time_window && !newRule.end_of_time_window) {
            alert($_('time_window_form_fill_required'));
            return;
        }

        if (!geocodedAddressData) {
            alert($_('geocode_first'));
            return;
        }

        // Sprawdź czy reguła już istnieje
        if (existingRuleForAddress) {
            alert($_('rule_already_exists_no_duplicate'));
            return;
        }

        loading = true;
        createMessage = '';
        error = '';

        // Create or get address from geocoded data
        JobModifyTimeWindowRuleApi.createOrGetAddress(get(api), geocodedAddressData)
            .then(addressResponse => {
                newRule.address_id = addressResponse.id;

                // Now create the rule
                return JobModifyTimeWindowRuleApi.createOne(get(api), newRule);
            })
            .then(() => {
                loading = false;
                createMessage = $_('rule_created');

                // Wywołaj callback onCreate i zamknij modal po krótkiej chwili
                setTimeout(() => {
                    console.log('Rule created, reloading list (add)');
                    componentProps.onCreate();
                    closeModal();
                }, 500);
            })
            .catch(err => {
                error = err;
                loading = false;
                alert($_('create_rule_error', { values: { err } }));
            });
    }

    async function checkIfRuleExists(addressId: number) {
        try {
            const filter = new ApiFilterBuilder(null);
            filter.addFilterByNumber('address_id', addressId);
            const existingRules = await JobModifyTimeWindowRuleApi.filter(get(api), filter);

            if (existingRules && existingRules.length > 0) {
                existingRuleForAddress = existingRules[0];
                ruleExistsWarning = $_('rule_exists_for_address', {
                    values: {
                        id: existingRuleForAddress.id,
                        start: existingRuleForAddress.start_of_time_window || '-',
                        end: existingRuleForAddress.end_of_time_window || '-',
                    },
                });
                return true;
            }

            existingRuleForAddress = null;
            ruleExistsWarning = '';
            return false;
        } catch (err) {
            console.error('Error checking existing rule:', err);
            return false;
        }
    }

    async function handleGeocode() {
        loading = true;
        createMessage = '';
        ruleExistsWarning = '';
        existingRuleForAddress = null;

        console.log('geocode_btn');

        try {
            const res = await JobModifyTimeWindowRuleApi.geocodeByAddress(
                $api,
                addressToFind.city,
                addressToFind.street,
                addressToFind.house,
                addressToFind.pcode
            );

            houseGeoId = res.geocoded_address.house_geoId;
            geocodedAddressData = res.geocoded_address;
            console.log('geocodeByAddress', res);

            // Sprawdź czy adres już istnieje i czy ma regułę
            if (houseGeoId) {
                const addressId = parseInt(houseGeoId);
                await checkIfRuleExists(addressId);
            }
        } catch (err) {
            console.error('geocodeByAddress', err);
            houseGeoId = null;
            geocodedAddressData = null;
            error = $_('geocode_error');
        } finally {
            loading = false;
        }
    }
</script>


<TemplateSettingsGeneral>
    <div slot="label">{$_('enter_address_for_time_window_rule')}</div>
    <div slot="content">
        <SettingsRow>
            <SettingsRowLabel>{$_('postal_code_label')}</SettingsRowLabel>
            <SettingsRowValue>
                <InputText
                    placeholder={$_('postal_code_label')}
                    bind:value={addressToFind.pcode}
                />
            </SettingsRowValue>
        </SettingsRow>
        <SettingsRow>
            <SettingsRowLabel>{$_('city_label')}</SettingsRowLabel>
            <SettingsRowValue>
                <InputText
                    placeholder={$_('city_label')}
                    bind:value={addressToFind.city}
                />
            </SettingsRowValue>
        </SettingsRow>
        <SettingsRow>
            <SettingsRowLabel>{$_('street_label')}</SettingsRowLabel>
            <SettingsRowValue>
                <InputText
                    placeholder={$_('street_label')}
                    bind:value={addressToFind.street}
                />
            </SettingsRowValue>
        </SettingsRow>
        <SettingsRow>
            <SettingsRowLabel>{$_('house_number_label')}</SettingsRowLabel>
            <SettingsRowValue>
                <InputText
                    placeholder={$_('house_number_label')}
                    bind:value={addressToFind.house}
                />
            </SettingsRowValue>
        </SettingsRow>
        <SettingsRow>
            <div>
                <ButtonSmallBlue text={$_('geocode_btn')} on:click={handleGeocode} disabled={loading} />
            </div>
        </SettingsRow>
    </div>
</TemplateSettingsGeneral>

{#if houseGeoId}
    <TemplateSettingsGeneral>
        <div slot="label">{$_('create_rule_for_address_id', { values: { houseGeoId } })}</div>
        <div slot="content">
            {#if ruleExistsWarning}
                <SettingsRow>
                    <div style="padding: 10px; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404;">
                        <strong>⚠️ {$_('warning_label')}</strong> {ruleExistsWarning}
                    </div>
                </SettingsRow>
            {/if}
            <SettingsRow>
                <SettingsRowLabel>{$_('rule_enabled')}</SettingsRowLabel>
                <SettingsRowValue>
                    <Switch bind:checked={newRule.enabled} label="" />
                </SettingsRowValue>
            </SettingsRow>
<!--            <SettingsRow>-->
<!--                <SettingsRowLabel>Część nazwy</SettingsRowLabel>-->
<!--                <SettingsRowValue>-->
<!--                    <InputText-->
<!--                        placeholder="Część nazwy"-->
<!--                        bind:value={newRule.name_part}-->
<!--                    />-->
<!--                </SettingsRowValue>-->
<!--            </SettingsRow>-->
            <SettingsRow>
                <SettingsRowLabel>{$_('from_label')}</SettingsRowLabel>
                <SettingsRowValue>
                    <SelectHour withBorder={false} hour={newRule.start_of_time_window || ''}
                                canBeEmpty={true}
                                onChange={h => {
                                newRule.start_of_time_window = h || null;
                                return true;
                            }} />
                </SettingsRowValue>
            </SettingsRow>
            <SettingsRow>
                <SettingsRowLabel>{$_('to_label')}</SettingsRowLabel>
                <SettingsRowValue>
                    <SelectHour withBorder={false} hour={newRule.end_of_time_window || ''}
                                canBeEmpty={true}
                                onChange={h => {
                                newRule.end_of_time_window = h || null;
                                return true;
                            }} />
                </SettingsRowValue>
            </SettingsRow>
            <SettingsRow>
                <div>
                    <ButtonSmallBlue
                        text={$_('save_rule')}
                        on:click={handleCreate}
                        disabled={loading || createMessage!=='' || existingRuleForAddress !== null}
                    />
                </div>
            </SettingsRow>
            {#if createMessage}
                <SuccessInline text={createMessage} />
            {/if}
            {#if error}
                <ErrorInline text={error} />
            {/if}
        </div>
    </TemplateSettingsGeneral>
{/if}

<div style="height: 100%"></div>

<!--    {#if houseGeoId}-->
<!--        <div style="display: flex; flex-direction: column; gap: 10px; width: 100%; justify-content: flex-start;">-->
<!--            <div>-->
<!--                Adres ma id: {houseGeoId}-->
<!--            </div>-->
<!--            <div style="display: flex; flex-direction: row; column-gap: 10px; align-items: center;">-->
<!--                <div class="col-enabled">-->
<!--                    <Switch bind:checked={newRule.enabled} label=""-->
<!--                            on:change={e => {-->
<!--                                        newRule.enabled = e.detail.checked;-->
<!--                                    }}-->
<!--                    />-->
<!--                </div>-->
<!--                &lt;!&ndash;                        <div class="col-name">&ndash;&gt;-->
<!--                &lt;!&ndash;                            {rule.name_part ?? '-'}&ndash;&gt;-->
<!--                &lt;!&ndash;                        </div>&ndash;&gt;-->
<!--                <div class="col-time" style="display: flex; flex-direction: row; column-gap: 10px; align-items: center;">-->
<!--                    <SelectHour withBorder={false} hour={newRule.start_of_time_window || ''} onChange={h => {-->
<!--                                newRule.start_of_time_window = h || null;-->
<!--                                return true;-->
<!--                            }} />-->
<!--                    - -->
<!--                    <SelectHour withBorder={false} hour={newRule.end_of_time_window || ''} onChange={h => {-->
<!--                                newRule.end_of_time_window = h || null;-->
<!--                                return true;-->
<!--                            }} />-->
<!--                </div>-->
<!--            </div>-->
<!--            <div>-->
<!--                <ButtonSmallBlue text="Zapisz regułę" on:click={handleCreate} disabled={loading || createMessage!==''} />-->
<!--            </div>-->
<!--            {#if createMessage}-->
<!--                <SuccessInline text={createMessage} />-->
<!--            {/if}-->
<!--            {#if error}-->
<!--                <ErrorInline text={error} />-->
<!--            {/if}-->
<!--        </div>-->
<!--    {/if}-->
<!--</div>-->